import { addToast } from "@caprikorn/dynamo-ui";
import type { FetchiosError, FetchiosResponse } from "@caprikorn/dynamo-utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { apiEcmsHttp } from "../http";
import { QUERY_KEYS } from "../keys";
import type {
  GetCustomerContract,
  PatchCustomerContractParams,
} from "./apiEcms.types";

const patchCustomerContract = ({
  customerId,
  contractId,
  addressingCode,
}: PatchCustomerContractParams) =>
  apiEcmsHttp.patch<GetCustomerContract>(
    `/customers/${customerId}/contracts/${contractId}`,
    { contractInformation: { addressingCode } },
  );

export const usePatchCustomerContract = () => {
  const queryClient = useQueryClient();
  const { currentGroup } = useGroupReferences();
  const { t } = useCustomTranslation(["main", "common"]);

  return useMutation<
    FetchiosResponse<GetCustomerContract>,
    FetchiosError,
    PatchCustomerContractParams
  >({
    mutationFn: patchCustomerContract,
    retry: false,
    mutationKey: [QUERY_KEYS.CONTRACT_ADDRESSING_CODE_EDITION, currentGroup],
    onSuccess: (_, { customerId, contractId }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CONTRACT, customerId, contractId, currentGroup],
      });
      addToast({
        variant: "success",
        title: t("perimeter.overview.site.eInvoicing.update.success"),
      });
    },
    onError: (error) => {
      if (error.status === 409 || error.status === 422) {
        addToast({
          variant: "error",
          title: t("perimeter.overview.site.eInvoicing.update.notEligible"),
        });
        return;
      }
      addToast({
        variant: "error",
        title: t("errors.title.large", { ns: "common" }),
      });
    },
  });
};
